import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useUpdateVendor } from '@/hooks/queries/useVendors';
import type { VendorResponse } from '@signalvote/shared';

interface RenameVendorDialogProps {
  vendor: VendorResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RenameVendorDialog({ vendor, open, onOpenChange }: RenameVendorDialogProps) {
  const [name, setName] = useState('');
  const updateVendor = useUpdateVendor();

  useEffect(() => {
    if (vendor && open) {
      setName(vendor.name);
    }
  }, [vendor, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !vendor) return;

    updateVendor.mutate(
      { vendorId: vendor.id, data: { name: name.trim() } },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const handleClose = () => {
    setName('');
    onOpenChange(false);
  };

  const isUnchanged = name.trim() === vendor?.name;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[400px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Rename Vendor</DialogTitle>
            <DialogDescription>
              Change the name of this vendor company.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="space-y-2">
              <label htmlFor="rename-vendor-name" className="text-sm font-medium">
                Vendor Name <span className="text-destructive">*</span>
              </label>
              <Input
                id="rename-vendor-name"
                placeholder="e.g., FieldWorks LLC"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isUnchanged || updateVendor.isPending}>
              {updateVendor.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
